import { motion } from 'framer-motion';
import { useInView } from 'framer-motion';
import { useRef } from 'react';
import { ArrowUpRight } from 'lucide-react';

const portfolioItems = [
  {
    id: '1',
    title: 'Nautical Marine Malta Ltd.',
    subtitle: 'Commissioned design used at a boat show',
    category: 'Graphic Design',
    client: 'Giovann Xerri',
    image: '/images/img1.jpg',
    span: 'col-span-1 md:col-span-2 row-span-1 md:row-span-2',
  },
  {
    id: '2',
    title: 'Għarb Local Council',
    subtitle: 'Designed for community events led by the Mayor of Għarb',
    category: 'Graphic Design',
    client: 'David Apap Agius',
    image: '/images/img2.jpg',
    span: 'col-span-1 row-span-1',
  },
  {
    id: '3',
    title: 'NTG Excavations',
    subtitle: 'Vehicle branding design for NTG Excavations',
    category: 'Graphic Design',
    client: 'Noel Grima',
    image: '/images/img3.jpg',
    span: 'col-span-1 row-span-1',
  },
  /* Template{
    id: '4',
    title: 'Dazzled Nails by Demi',
    subtitle: 'Gift Voucher Design for Dazzled Nails by Demi',
    category: 'Graphic Design',
    client: 'Demi Anne',
    image: '/images/img5.jpg',
    span: 'col-span-1 row-span-1',
  },*/
  {
    id: '4',
    title: 'Dazzled Nails by Demi',
    subtitle: 'Gift Voucher Design for Dazzled Nails by Demi',
    category: 'Graphic Design',
    client: 'Demi Anne',
    image: '/images/img5.jpg',
    span: 'col-span-1 row-span-1',
  },
  {
    id: '5',
    title: 'NTG Excavations Website',
    subtitle: 'Website Designed & Developed for NTG Excavations',
    category: 'Web Design & Development',
    client: 'Noel Grima',
    image: '/images/img4.jpg',
    span: 'col-span-1 md:col-span-2 row-span-1 md:row-span-1',
  },
      {
    id: '6',
    title: 'Għarb Matchday Poster',
    subtitle: 'Matchday Poster for Għarb Rangers',
    category: 'Sports Design',
    client: 'Għarb Rangers',
    image: '/images/img7.jpg',
    span: 'col-span-2 row-span-1',
  },
  {
    id: '7',
    title: 'CX Social Media',
    subtitle: 'Full rebrand project including logo and Facebook cover assets',
    category: 'Digital Design',
    client: 'Christian Xuereb',
    image: '/images/img6.jpg',
    span: 'col-span-1 row-span-1',
  },

];

const PortfolioItem = ({ item, index }) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-100px' });

  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 40 }}
      animate={isInView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.6, delay: index * 0.1 }}
      className={`${item.span} portfolio-item group cursor-pointer`}
      data-testid={`portfolio-item-${item.id}`}
    >
      <div className="relative h-full min-h-[280px] md:min-h-[320px] overflow-hidden rounded-lg border border-border/30">
        <img
          src={item.image}
          alt={item.title}
          className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
        />
        
        {/* Overlay gradient */}
        <div className="absolute inset-0 bg-gradient-to-t from-background/90 via-background/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
        
        {/* Content */}
        <div className="absolute bottom-0 left-0 right-0 p-6 translate-y-full group-hover:translate-y-0 transition-transform duration-300">
          <span className="text-xs text-primary font-medium tracking-wider uppercase">
            {item.category}
          </span>
          <h3 className="text-xl font-['Sora'] font-semibold text-foreground mt-1 flex items-center gap-2">
            {item.title}
            <ArrowUpRight className="w-5 h-5 opacity-0 group-hover:opacity-100 transition-opacity" />
          </h3>
          <p className="text-sm text-muted-foreground mt-1">{item.client}</p>
        </div>
        
        {/* Category badge - always visible */}
        <div className="absolute top-4 left-4">
          <span className="text-xs bg-background/80 backdrop-blur-sm text-foreground px-3 py-1.5 rounded-full border border-border/30">
            {item.category}
          </span>
        </div>
      </div>
    </motion.div>
  );
};

export const SelectedWork = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-100px' });

  return (
    <section
      id="work"
      data-testid="selected-work-section"
      className="py-24 md:py-32 px-6 lg:px-8 relative"
    >
      <div className="max-w-7xl mx-auto">
        {/* Section Header */}
        <motion.div
          ref={ref}
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
          className="mb-16"
        >
          <span className="text-primary text-sm font-medium tracking-wider uppercase">
            Portfolio
          </span>
          <h2
            data-testid="selected-work-title"
            className="font-['Sora'] text-3xl sm:text-4xl lg:text-5xl font-bold text-foreground mt-2 tracking-tight"
          >
            Selected Client Work
          </h2>
          <p className="text-muted-foreground mt-4 max-w-lg">
            Real projects delivered for real clients. Each piece crafted with strategy, precision, and purpose.
          </p>
        </motion.div>

        {/* Portfolio Grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-6">
          {portfolioItems.map((item, index) => (
            <PortfolioItem key={item.id} item={item} index={index} />
          ))}
        </div>
      </div>
    </section>
  );
};

export default SelectedWork;
